import { assertNever } from '@kofno/piper';
import { fromNullable, just, Maybe, nothing } from 'maybeasy';
import { ShareState } from './Stores/ShareStore/Types';

// DVLP-7749: what this invite is for. A collection invite grants access to
// everything the collection carries; a course invite enrolls people directly
// in one program of it. The screen names which one it is everywhere the admin
// reads it: header, section copy, the participant picker and the result.
export interface CollectionInvite {
  kind: 'collection';
  collectionName: string;
}

export interface CourseInvite {
  kind: 'course';
  // Nothing until the admin picks the course on the invite page (DVLP-7976).
  courseName: Maybe<string>;
  collectionName: string;
}

export type InviteTarget = CollectionInvite | CourseInvite;

export const inviteTarget = (state: ShareState): Maybe<InviteTarget> => {
  switch (state.kind) {
    case 'waiting':
    case 'loading':
    case 'error':
      return nothing();
    case 'ready':
    case 'searching':
    case 'reviewing':
    case 'refreshing':
    case 'publishing':
    case 'submitting':
    case 'confirming': {
      const collection = state.learningCollectionResource.payload;
      const collectionInvite: InviteTarget = {
        kind: 'collection',
        collectionName: collection.name,
      };
      switch (state.shareType) {
        case 'collection':
          return just(collectionInvite);
        case 'program':
          return just<InviteTarget>({
            kind: 'course',
            courseName: state.programId
              .andThen((programId) =>
                fromNullable(collection.programs.find((p) => p.id === programId)),
              )
              .map((program) => program.programFamily.name),
            collectionName: collection.name,
          });
        default:
          return assertNever(state.shareType);
      }
    }
    default:
      return assertNever(state);
  }
};

export const foldInviteTarget = <A>(
  target: InviteTarget,
  onCollection: (invite: CollectionInvite) => A,
  onCourse: (invite: CourseInvite) => A,
): A => {
  switch (target.kind) {
    case 'collection':
      return onCollection(target);
    case 'course':
      return onCourse(target);
    default:
      return assertNever(target);
  }
};
